"use client"

import { memo, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"

type Props = {
  image?: string
  fallback?: string
  blur?: number
  opacity?: number
  keyId?: string | number
}

function DrawerBackground({
  image,
  fallback = "/image/app/logo.jpg",
  blur = 40,
  opacity = 0.45,
  keyId,
}: Props) {
  const bg = useMemo(() => image || fallback, [image, fallback])

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* LAYER 1: IMAGE BLURRED */}
      <AnimatePresence mode="wait">
        <motion.div
          key={keyId || bg}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1.2 }}
          exit={{ opacity: 0, scale: 1.15 }}
          transition={{
            duration: 0.45,
            ease: [0.16, 1, 0.3, 1],
          }}
          className="absolute inset-0 will-change-transform"
          style={{
            backgroundImage: `url(${bg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transform: "translateZ(0)",
          }}
        />
      </AnimatePresence>

      {/* LAYER 2: HEAVY BLUR (GPU optimized overlay) */}
      <div
        className="absolute inset-0"
        style={{
          backdropFilter: `blur(${blur}px)`,
          WebkitBackdropFilter: `blur(${blur}px)`,
          background:
            "radial-gradient(circle at center, rgba(0,0,0,0.25), rgba(0,0,0,0.75))",
          transform: "translateZ(0)",
        }}
      />

      {/* LAYER 3: DARK OVERLAY (contrast control) */}
      <div
        className="absolute inset-0"
        style={{
          backgroundColor: `rgba(0,0,0,${opacity})`,
        }}
      />

      {/* LAYER 4: VIGNETTE (premium feel depth) */}
      <div
        className="
          absolute inset-0
          pointer-events-none
          bg-[radial-gradient(circle_at_center,transparent_40%,rgba(0,0,0,0.65))]
        "
      />
    </div>
  )
}

export default memo(DrawerBackground)